import React      from 'react';
import PropTypes  from 'prop-types';
import { Link }   from 'react-router-dom';
import './header.module.css';

function getCurrentPath() {
  const hash = window.location.hash;
  if (hash) return hash.replace(/^#/, '').split('?')[0] || '/';
  return window.location.pathname.split('?')[0] || '/';
}

/* ── Inline SVG Icons ─────────────────────────────────────────── */

const HomeIcon = () => (
  <svg className="app-hdr__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const BellIcon = () => (
  <svg className="app-hdr__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const GalleryIcon = () => (
  <svg className="app-hdr__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

const TrophyIcon = () => (
  <svg className="app-hdr__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="8 2 12 6 16 2"/>
    <path d="M6 6H4a2 2 0 0 0-2 2v2a4 4 0 0 0 4 4"/>
    <path d="M18 6h2a2 2 0 0 1 2 2v2a4 4 0 0 1-4 4"/>
    <path d="M12 14v4"/>
    <path d="M8 18h8"/>
    <path d="M6 6v6a6 6 0 0 0 12 0V6"/>
  </svg>
);

const InventoryIcon = () => (
  <svg className="app-hdr__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const LogOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

/* ── Pure presentational sub-components ──────────────────────── */

const NavButton = ({ to, icon, label, isActive }) => (
  <Link
    to={to}
    className={'app-hdr__nav-btn' + (isActive ? ' app-hdr__nav-btn--active' : '')}
  >
    {icon}
    <span className="app-hdr__nav-label">{label}</span>
  </Link>
);

const LogoutButton = ({ onLogout }) => (
  <Link to="/login" onClick={onLogout} className="app-hdr__logout">
    <LogOutIcon />
    Logout
  </Link>
);

const HamburgerButton = ({ onClick }) => (
  <button className="app-hdr__hamburger" onClick={onClick} aria-label="Toggle side menu">
    <span className="app-hdr__hamburger-bar" />
    <span className="app-hdr__hamburger-bar" />
    <span className="app-hdr__hamburger-bar" />
  </button>
);

/* ── Main Header (React 15-compatible class component) ────────── */

class Header extends React.Component {
  constructor(props) {
    super(props);
    this.state = { pathname: getCurrentPath() };
    this._onRouteChange = this._onRouteChange.bind(this);
  }

  _onRouteChange() {
    this.setState({ pathname: getCurrentPath() });
  }

  componentDidMount() {
    window.addEventListener('hashchange', this._onRouteChange);
    window.addEventListener('popstate',   this._onRouteChange);
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this._onRouteChange);
    window.removeEventListener('popstate',   this._onRouteChange);
  }

  render() {
    const { appName, toggleSideMenu, onLogout, accessConfig } = this.props;
    const { pathname } = this.state;

    const showEvents =
      localStorage.getItem('permissions') &&
      localStorage.getItem('permissions').includes('access_cm_event') &&
      accessConfig !== 'Technichal Partner';

    const showInventory =
      localStorage.getItem('permissions') &&
      localStorage.getItem('permissions').includes('access_cm_inventory') &&
      accessConfig !== 'Technichal Partner';

    const showAnnouncements =
      localStorage.getItem('access') === 'Trespasser' &&
      accessConfig !== 'Technichal Partner';

    return (
      <header className="app-hdr__root">
        <div className="app-hdr__container">
          <div className="app-hdr__inner">

            {/* Left: Hamburger + Logo */}
            <div className="app-hdr__left">
              <HamburgerButton onClick={toggleSideMenu} />
              <Link to="/" className="app-hdr__logo">
                <span>{appName}</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="app-hdr__nav">
              <NavButton
                to="/"
                icon={<HomeIcon />}
                label="Home"
                isActive={pathname === '/'}
              />

              {showAnnouncements && (
                <NavButton
                  to="/announcements"
                  icon={<BellIcon />}
                  label="Announcements"
                  isActive={pathname === '/announcements'}
                />
              )}

              <NavButton
                to="/crud/gallery"
                icon={<GalleryIcon />}
                label="Gallery"
                isActive={pathname === '/crud/gallery'}
              />

              {showEvents && (
                <NavButton
                  to="/partnerTournament"
                  icon={<TrophyIcon />}
                  label="Events"
                  isActive={pathname === '/partnerTournament'}
                />
              )}

              {showInventory && (
                <NavButton
                  to="/inventory"
                  icon={<InventoryIcon />}
                  label="Inventory"
                  isActive={pathname === '/inventory'}
                />
              )}
            </nav>

            {/* Logout */}
            <LogoutButton onLogout={onLogout} />

          </div>
        </div>
      </header>
    );
  }
}

Header.propTypes = {
  appName:        PropTypes.string,
  onLogout:       PropTypes.func,
  currentView:    PropTypes.string,
  toggleSideMenu: PropTypes.func,
  accessConfig:   PropTypes.string,
};

Header.defaultProps = {
  appName: 'Events Portal',
};

Header.displayName = 'Header';

export default Header;


// WEBPACK FOOTER //
// ./src/app/components/header/Header.js